import { Box, Button, Grid, Typography } from "@mui/material"
import dayjs from "dayjs"
import React, { useEffect, useState } from "react"
import LayoutPage from "src/components/page-layout/layout-page"
import SelectValue from "src/components/select-value"
import PriceService from "src/services/price-service"
import { DATE_FORMAT, DATE_FORMAT_SHOW } from "src/utils/constants"
import AlertMessage from "src/components/alert-message"
import {
    formatDecimalCaseReplaceDot,
    formatDecimalCaseWithComma,
    formatStringToFloatDecimalCase
} from "src/utils/format-decimal"
import ParameterPaginationTable from "src/components/table/parameter-pagination-table"

const PriceUpdateSpreadsPage = () => {
    const [loading, setLoading] = useState(false)
    const [dateList, setDateList] = useState([])
    const [selectedDate, setSelectedDate] = useState("")
    const [spreadList, setSpreadList] = useState([])
    const [pagination, setPagination] = useState({ count: 0, next: null, previous: null })
    const [keyTable, setKeyTable] = useState(Math.random() * 10)
    const [resetPaginationKey, setResetPaginationKey] = useState(0)
    const [activeFilters, setActiveFilters] = useState("") 
    const [alertMessageState, setAlertMessageState] = React.useState({
        open: false,
        message: "",
        type: ""
    })

    const priceService = new PriceService()

    useEffect(() => {
        loadDates()
    }, [])

    useEffect(() => {
        if (selectedDate && dayjs(selectedDate).isValid()) {
            loadSpreads()
        }
    }, [selectedDate])

    const loadDates = () => {
        setLoading(true)
        priceService.getLastDatesOcurrence().then((response) => {
            const { body: { data } } = response
            const dateList = data.map((d) => dayjs(d))
            const sortedList = dateList.sort((a, b) => b - a)
            const options = sortedList.map((item) => ({ value: item, description: item.format(DATE_FORMAT_SHOW) }))
            setDateList(options)
            if (sortedList.length > 0) {
                setSelectedDate(sortedList[0])
            }
            setLoading(false)
        }).catch(() => {
            setLoading(false)
            openAlert('Tivemos um problema ao carregar as datas, tente novamente', 'error')
        })
    }

    const loadSpreads = (offset = 0, limit = 25) => {
        if (!selectedDate) return

        setLoading(true)
        const dateFormatted = selectedDate.format(DATE_FORMAT)

        priceService.getUpdateSpreads(dateFormatted, limit, offset)
            .then((response) => {
                const { body } = response

                if (body && (body.results || body.data)) {
                    const results = body.results || body.data

                    const rows = results.map((obj, index) => ({
                        ...obj,
                        id: obj.ID || `${obj.CETIP || 'item'}-${index}`
                    }))

                    setSpreadList(rows)
                    setPagination({
                        count: body.count || results.length,
                        next: body.next || null,
                        previous: body.previous || null
                    })
                } else {

                    setSpreadList([])
                    setPagination({ count: 0, next: null, previous: null })
                    openAlert('Nenhum dado encontrado ou formato incorreto', 'warning')
                }
            })
            .catch((error) => {

                setSpreadList([])
                setPagination({ count: 0, next: null, previous: null })
                openAlert('Erro ao carregar dados: ' + (error.message || 'Erro desconhecido'), 'error')
            })
            .finally(() => {
                setLoading(false)
            })
    }

    const changePage = async (endpoint) => {
        setLoading(true)
        try {
            const response = await priceService.paginate(endpoint)
            const { body } = response
            const { results, count, previous, next } = body

            const list = results.map((obj, index) => ({
                ...obj,
                id: obj.ID || `${obj.CETIP}-${index}`
            }))

            setPagination({ count: count, next: next, previous: previous })
            setSpreadList([])
            setSpreadList(list)
        } catch (error) {

            openAlert('Erro ao carregar a próxima página', 'error')
        } finally {
            setLoading(false)
        }
    }

    const onFilter = async (filterList, limit = 25, offset = 0) => {
        if (!selectedDate) return

        setLoading(true)
        const dateFormatted = selectedDate.format(DATE_FORMAT)
        const query = filterList.map(obj => `${encodeURIComponent(obj.field)}=${encodeURIComponent(obj.value)}`).join('&')

        setActiveFilters(query)

        try {
            const response = await priceService.getUpdateSpreads(dateFormatted, limit, offset, query)
            const { body } = response

            if (body && body.results) {
                const rows = body.results.map((obj, index) => ({
                    ...obj,
                    id: obj.ID || `${obj.CETIP}-${index}`
                }))
                setPagination({
                    count: body.count || 0,
                    next: body.next || null,
                    previous: body.previous || null
                })
                setSpreadList(rows)
                setResetPaginationKey(prev => prev + 1)
            }
        } catch (error) {

            setSpreadList([])
            setPagination({ count: 0, next: null, previous: null })
            openAlert('Erro ao aplicar filtros', 'error')
        } finally {
            setLoading(false)
        }
    }

    const formatDate = (params) => {
        const parsedData = dayjs(params.value)
        if (parsedData.isValid()) {
            return parsedData.format(DATE_FORMAT_SHOW)
        } else {
            return ''
        }
    }

    const changeDate = (value) => {
        setPagination({ count: 0, next: null, previous: null })
        setKeyTable(Math.random() * 10)
        setSelectedDate(value)
    }

    const exportCSV = () => {
        setLoading(true)
        priceService.exportUpdateSpreads(selectedDate.format(DATE_FORMAT), activeFilters)
            .then((response) => {
                const blob = response.body

                const downloadUrl = window.URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.style.display = 'none'
                a.href = downloadUrl
                a.download = `atualizacao-spreads-${selectedDate.format(DATE_FORMAT)}.csv`
                document.body.appendChild(a)
                a.click()
                window.URL.revokeObjectURL(downloadUrl)
            })
            .catch(() => {
                openAlert('Tivemos um problema ao exportar os dados.', 'error')
            })
            .finally(() => {
                setLoading(false)
            })
    }

    const closeAlert = () => {
        setAlertMessageState({ ...alertMessageState, open: false })
    }

    const openAlert = (message, type) => {
        setAlertMessageState({
            ...alertMessageState,
            open: true,
            message: message,
            type: type
        })
    }

    const renderTextColumn = (params) =>
        <Box sx={{ whiteSpace: 'pre-line', height: 'auto' }}>
            <Typography variant="p">{params.value}</Typography>
        </Box>

    const columns = [
        {
            field: 'DataReferencia',
            headerName: 'Data Referência',
            flex: true,
            minWidth: 150,
            maxWidth: 150,
            typeComponent: 'date',
            disableColumnMenu: true,
            editable: false,
            renderCell: () => selectedDate.format(DATE_FORMAT_SHOW)
        },
        {
            field: 'CETIP',
            headerName: 'CETIP',
            flex: true,
            minWidth: 150,
            maxWidth: 150,
            disableColumnMenu: true,
            editable: false
        },
        {
            field: 'TipoAtivo',
            headerName: 'Tipo',
            flex: 1,
            minWidth: 120,
            maxWidth: 150,
            disableColumnMenu: true,
            editable: false
        },
        {
            field: 'Devedor',
            headerName: 'Devedor',
            flex: 1,
            minWidth: 200,
            maxWidth: 200,
            renderCell: renderTextColumn,
            disableColumnMenu: true,
            editable: false
        },
        {
            field: 'Setor',
            headerName: 'Setor',
            flex: 1,
            minWidth: 200,
            maxWidth: 300,
            renderCell: renderTextColumn,
            disableColumnMenu: true,
            editable: false
        },
        {
            field: 'SubSetor',
            headerName: 'SubSetor',
            flex: 1,
            minWidth: 100,
            maxWidth: 150,
            renderCell: renderTextColumn,
            disableColumnMenu: true,
            editable: false
        },
        {
            field: 'ValidadeRating',
            headerName: 'Validade Rating',
            flex: 1,
            minWidth: 200,
            maxWidth: 200,
            renderCell: formatDate,
            typeComponent: 'date',
            disableColumnMenu: true,
            editable: false
        },
        {
            field: 'Duration',
            headerName: 'Duration',
            flex: 1,
            minWidth: 150,
            maxWidth: 150,
            renderCell: (params) => formatDecimalCaseWithComma(params.value),
            disableColumnMenu: true,
            typeComponent: 'number',
            type: 'number',
            editable: false
        },
        {
            field: 'SpreadAtual',
            headerName: 'Spread Atual',
            flex: 1,
            minWidth: 150,
            maxWidth: 150,
            typeComponent: 'number',
            type: 'number',
            renderCell: (params) => formatStringToFloatDecimalCase(formatDecimalCaseReplaceDot(params.value)),
            disableColumnMenu: true,
            editable: false
        },
        {
            field: 'SpreadAnterior',
            headerName: 'Spread Anterior',
            flex: 1,
            minWidth: 150,
            maxWidth: 150,
            typeComponent: 'number',
            type: 'number',
            renderCell: (params) => formatStringToFloatDecimalCase(formatDecimalCaseReplaceDot(params.value)),
            disableColumnMenu: true,
            editable: false
        },
        {
            field: 'DeltaSpread',
            headerName: 'Delta Spread',
            flex: 1,
            minWidth: 150,
            maxWidth: 150,
            typeComponent: 'number', type: 'number',
            renderCell: (params) => formatStringToFloatDecimalCase(formatDecimalCaseReplaceDot(params.value)),
            disableColumnMenu: true,
            editable: false
        },
        {
            field: 'VarPUMTM',
            headerName: 'Var PUMTM',
            flex: 1,
            minWidth: 150,
            maxWidth: 150,
            typeComponent: 'number', type: 'number',
            renderCell: (params) => formatStringToFloatDecimalCase(formatDecimalCaseReplaceDot(params.value)),
            disableColumnMenu: true,
            editable: false
        },
        {
            field: 'DeltaScoreRating',
            headerName: 'Delta Score Rating',
            flex: 1,
            minWidth: 150,
            maxWidth: 150,
            typeComponent: 'number',
            type: 'number',
            disableColumnMenu: true,
            editable: false
        },
        {
            field: 'AnaliseCreditoEmissao',
            sortable: false,
            minWidth: 300,
            maxWidth: 600,
            disableColumnMenu: true,
            editable: false,
            renderCell: renderTextColumn
        },
        {
            field: 'AnaliseCreditoDevedor',
            headerName: 'Análise Crédito Devedor',
            flex: 1,
            sortable: false,
            minWidth: 300,
            maxWidth: 600,
            renderCell: renderTextColumn,
            disableColumnMenu: true,
            editable: false
        },
        {
            field: 'AnaliseSpreadIndicativo',
            headerName: 'Análise Spreads Indicativos',
            flex: 1,
            sortable: false,
            minWidth: 300,
            maxWidth: 600,
            renderCell: renderTextColumn,
            disableColumnMenu: true,
            editable: false
        },
    ]

    return <LayoutPage loading={loading} title="Atualização de Spreads">
        <AlertMessage open={alertMessageState.open} message={alertMessageState.message} type={alertMessageState.type}
            close={closeAlert} />
        <Grid sx={{ mr: { md: 13, xs: 0 } }}>
            <Grid container
                direction="row"
                justifyContent="flex-end"
                alignItems="center" spacing={3}>
                <Grid item xl={2} md={6} xs={6}>
                    <SelectValue
                        label={"Data Referência"}
                        value={selectedDate}
                        id="select-app"
                        changeValue={changeDate}
                        options={dateList} />
                </Grid>
                <Grid item>
                    <Button variant="contained" onClick={exportCSV}>
                        Exportar para CSV
                    </Button>
                </Grid>
            </Grid>
        </Grid>
        <br />
        <Grid container direction="row" justifyContent="center" spacing={2}>
            <Grid item xl={12} md={12} xs={12}>

                <ParameterPaginationTable
                    key={keyTable}
                    columns={columns}
                    data={spreadList}
                    rowHeigth="auto"
                    setData={setSpreadList}
                    pagination={pagination}
                    onChangePage={changePage}
                    filterFunction={onFilter}
                    customFilter={true}
                    useDeleteButton={false}
                    resetPaginationKey={resetPaginationKey}
                />

            </Grid>
        </Grid>
    </LayoutPage>
}

export default PriceUpdateSpreadsPage